import { App as AntdApp, Alert, Button, Card, Divider, Empty, Input, Skeleton, Space, Tag, Typography, theme } from "antd";
import { SaveOutlined, SyncOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { TOKEN_KEY } from "../../authProvider";
import { apiUrl } from "../../consts";
import { axiosInstance } from "../../utils";
import "./training-questionnaire.css";

type QuestionnaireOption = {
  id: string;
  value: string;
  title: string;
  detail: string;
  position: number;
  active: boolean;
};

type QuestionnaireQuestion = {
  id: string;
  code: string;
  answerField: string;
  selectionMode: "single" | "multiple";
  minSelections: number;
  maxSelections: number;
  position: number;
  kicker: string;
  title: string;
  hint: string | null;
  active: boolean;
  options: QuestionnaireOption[];
};

type Questionnaire = {
  version: number;
  questions: QuestionnaireQuestion[];
};

type QuestionnairePayload = {
  questions: Array<{
    id: string;
    kicker: string;
    title: string;
    hint: string | null;
    options: Array<{
      id: string;
      title: string;
      detail: string;
    }>;
  }>;
};

const toPayload = (questions: QuestionnaireQuestion[]): QuestionnairePayload => ({
  questions: questions.map((question) => ({
    id: question.id,
    kicker: question.kicker,
    title: question.title,
    hint: question.hint?.trim() || null,
    options: question.options.map((option) => ({
      id: option.id,
      title: option.title,
      detail: option.detail,
    })),
  })),
});

const payloadKey = (questions: QuestionnaireQuestion[]) =>
  JSON.stringify(toPayload(questions));

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) ?? ""}`,
});

export const TrainingQuestionnairePage = () => {
  const { message } = AntdApp.useApp();
  const { token } = theme.useToken();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedPayload = useRef("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get<{ data: Questionnaire }>(
        `${apiUrl}/training-questionnaire`,
        { headers: authHeaders() },
      );
      setQuestionnaire(response.data.data);
      savedPayload.current = payloadKey(response.data.data.questions);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "The questionnaire could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const currentPayload = questionnaire
    ? payloadKey(questionnaire.questions)
    : "";
  const dirty = currentPayload !== savedPayload.current;
  const valid =
    questionnaire?.questions.every(
      (question) =>
        question.kicker.trim().length > 0 &&
        question.title.trim().length > 0 &&
        question.options.every(
          (option) =>
            option.title.trim().length > 0 && option.detail.trim().length > 0,
        ),
    ) ?? false;

  useEffect(() => {
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [dirty]);

  const updateQuestion = (
    questionId: string,
    field: "kicker" | "title" | "hint",
    value: string,
  ) => {
    setQuestionnaire((current) =>
      current
        ? {
            ...current,
            questions: current.questions.map((question) =>
              question.id === questionId
                ? { ...question, [field]: value }
                : question,
            ),
          }
        : current,
    );
  };

  const updateOption = (
    questionId: string,
    optionId: string,
    field: "title" | "detail",
    value: string,
  ) => {
    setQuestionnaire((current) =>
      current
        ? {
            ...current,
            questions: current.questions.map((question) =>
              question.id === questionId
                ? {
                    ...question,
                    options: question.options.map((option) =>
                      option.id === optionId
                        ? { ...option, [field]: value }
                        : option,
                    ),
                  }
                : question,
            ),
          }
        : current,
    );
  };

  const save = async () => {
    if (!questionnaire || !dirty || !valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await axiosInstance.put<{ data: Questionnaire }>(
        `${apiUrl}/training-questionnaire`,
        toPayload(questionnaire.questions),
        { headers: authHeaders() },
      );
      setQuestionnaire(response.data.data);
      savedPayload.current = payloadKey(response.data.data.questions);
      message.success("Training questionnaire updated");
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Changes could not be saved.",
      );
    } finally {
      setSaving(false);
    }
  };

  const pageStyle = useMemo(
    () =>
      ({
        "--tq-accent": token.colorPrimary,
        "--tq-border": token.colorBorderSecondary,
        "--tq-surface": token.colorBgContainer,
        "--tq-muted-surface": token.colorFillQuaternary,
        "--tq-text": token.colorText,
        "--tq-muted": token.colorTextSecondary,
      }) as CSSProperties,
    [token],
  );

  return (
    <main className="training-questionnaire" style={pageStyle}>
      <header className="training-questionnaire__header">
        <div>
          <Typography.Title level={2}>Training questionnaire</Typography.Title>
          <Typography.Paragraph type="secondary">
            Edit the words players see. Codes, answer values and ordering stay
            fixed so saved pathways continue to work.
          </Typography.Paragraph>
        </div>
        <Space>
          <Button icon={<SyncOutlined />} onClick={() => void load()} disabled={saving}>
            Reload
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => void save()}
            loading={saving}
            disabled={!dirty || !valid}
          >
            Save changes
          </Button>
        </Space>
      </header>

      {error && (
        <Alert
          type="error"
          showIcon
          message="Questionnaire update failed"
          description={error}
          closable
          onClose={() => setError(null)}
        />
      )}

      {loading ? (
        <Card className="training-questionnaire__loading">
          <Skeleton active paragraph={{ rows: 10 }} />
        </Card>
      ) : !questionnaire || questionnaire.questions.length === 0 ? (
        <Card>
          <Empty description="No training questions are configured." />
        </Card>
      ) : (
        <div className="training-questionnaire__sequence">
          {questionnaire.questions.map((question, index) => (
            <Card
              key={question.id}
              className="training-questionnaire__card"
              title={
                <div className="training-questionnaire__card-title">
                  <span className="training-questionnaire__step">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <Typography.Text strong>{question.code}</Typography.Text>
                    <div className="training-questionnaire__meta">
                      <Tag bordered={false}>{question.selectionMode}</Tag>
                      <Typography.Text type="secondary">
                        Choose {question.minSelections}
                        {question.maxSelections !== question.minSelections
                          ? `–${question.maxSelections}`
                          : ""}
                      </Typography.Text>
                    </div>
                  </div>
                </div>
              }
            >
              <div className="training-questionnaire__question-grid">
                <label>
                  <span>Kicker</span>
                  <Input
                    value={question.kicker}
                    maxLength={128}
                    onChange={(event) =>
                      updateQuestion(question.id, "kicker", event.target.value)
                    }
                  />
                </label>
                <label className="training-questionnaire__wide-field">
                  <span>Question</span>
                  <Input
                    value={question.title}
                    maxLength={255}
                    onChange={(event) =>
                      updateQuestion(question.id, "title", event.target.value)
                    }
                  />
                </label>
                <label className="training-questionnaire__wide-field">
                  <span>Helper text</span>
                  <Input
                    value={question.hint ?? ""}
                    maxLength={128}
                    placeholder="Optional"
                    onChange={(event) =>
                      updateQuestion(question.id, "hint", event.target.value)
                    }
                  />
                </label>
              </div>

              <Divider orientation="left" plain>
                Answer choices
              </Divider>

              <div className="training-questionnaire__options">
                {question.options.map((option) => (
                  <section
                    key={option.id}
                    className="training-questionnaire__option"
                    aria-label={`${option.value} answer copy`}
                  >
                    <Typography.Text code>{option.value}</Typography.Text>
                    <label>
                      <span>Label</span>
                      <Input
                        value={option.title}
                        maxLength={255}
                        onChange={(event) =>
                          updateOption(
                            question.id,
                            option.id,
                            "title",
                            event.target.value,
                          )
                        }
                      />
                    </label>
                    <label>
                      <span>Supporting text</span>
                      <Input
                        value={option.detail}
                        maxLength={255}
                        onChange={(event) =>
                          updateOption(
                            question.id,
                            option.id,
                            "detail",
                            event.target.value,
                          )
                        }
                      />
                    </label>
                  </section>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </main>
  );
};
